import { PrecisePriceConfig } from '@/entities/config';
import { isStorageWritable } from '@/common/helpers';
import { nanoid } from 'nanoid';

interface VinWriteupIdMap {
	[key: string]: string;
}

interface VinTimestampMap {
	[key: string]: number;
}

interface WebsiteIdMap {
	[key: string]: WebsiteData;
}

interface WebsiteData {
	settings: object;
	writeupMap: VinWriteupIdMap;
	warmupMap: VinTimestampMap;
}

export class StorageService {
	private readonly storage: Storage;

	private readonly websiteId: string;

	private readonly storageKey: string;

	private readonly clientIdKey: string;

	private readonly isWritable: boolean;

	private readonly warmupTimeout = 43200000; // 12 hours

	constructor({ websiteId }: PrecisePriceConfig, storage: Storage = window.localStorage as Storage) {
		this.storage = storage;
		this.websiteId = websiteId;
		this.storageKey = 'ppStorage';
		this.clientIdKey = 'ppClientId';
		this.isWritable = this.isStorageWritable();
		this.cleanWarmupMap();
	}

	public getClientId(): string {
		if (!this.isStorageWritable()) return nanoid();

		const id: string | null = this.storage.getItem(this.clientIdKey);

		if (id) { return id; }

		const newId = nanoid();

		this.storage.setItem(this.clientIdKey, newId);
		return newId;
	}

	public saveWriteupId(vin: string, writeupId: string): void {
		if (!this.isWritable) { return; }

		const data = this.getData();

		data[this.websiteId].writeupMap[vin] = writeupId;

		this.saveData(data);
	}

	public getWriteupId(vin: string): string | null {
		if (!this.isWritable) { return null; }

		const data = this.getData();

		return data[this.websiteId].writeupMap[vin] || null;
	}

	public saveSettings(settings: object): void {
		if (!this.isWritable) { return; }

		const data = this.getData();

		data[this.websiteId].settings = Object.assign(data[this.websiteId].settings, settings);

		this.saveData(data);
	}

	public getSettings(): object | null {
		if (!this.isWritable) { return null; }

		const data = this.getData();

		return data[this.websiteId].settings || null;
	}

	public isWarmedUp(vin: string): boolean {
		if (!this.isWritable) { return true; }

		const data = this.getData();
		const time = data[this.websiteId].warmupMap[vin];

		return (time && this.isWarmupActual(time)) || false;
	}

	public setWarmedUp(vin: string): undefined {
		if (!this.isWritable) { return; }

		const data = this.getData();

		data[this.websiteId].warmupMap[vin] = new Date().getTime();
		this.saveData(data);
	}

	private cleanWarmupMap() {
		if (!this.isWritable) { return; }

		const data = this.getData();
		const { warmupMap } = data[this.websiteId];

		Object.keys(warmupMap).forEach(vin => {
			if (!this.isWarmupActual(warmupMap[vin])) { delete warmupMap[vin]; }
		});

		this.saveData(data);
	}

	private saveData(data: WebsiteIdMap): void {
		let json = '';

		try {
			json = JSON.stringify(data);
		} catch (error) {
			console.error(error); // eslint-disable-line no-console

			return;
		}

		this.storage.setItem(this.storageKey, json);
	}

	private getData(): WebsiteIdMap {
		const { websiteId } = this;
		const defaultData: WebsiteIdMap = {
			[this.websiteId]: {
				writeupMap: {},
				settings: {},
				warmupMap: {},
			},
		};
		let data = defaultData;

		const json: string | null = this.storage.getItem(this.storageKey);

		if (json) {
			try {
				data = JSON.parse(json);
			} catch (error) {
				console.error(error); // eslint-disable-line no-console

				this.storage.removeItem(this.storageKey); // remove broken data

				return defaultData;
			}

			// TODO: Use io-ts validation
			if (!data[websiteId] || !data[websiteId].writeupMap || !data[websiteId].settings || !data[websiteId].warmupMap) {
				return defaultData;
			}
		}

		return data;
	}

	private isWarmupActual(timestamp: number) {
		return (new Date().getTime() - timestamp) < this.warmupTimeout;
	}

	private isStorageWritable(storage: Storage = window.localStorage): boolean {
		return isStorageWritable(storage, this.storageKey);
	}
}
